import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Incident } from 'core/models/jianList/incident';
import { useState, useEffect } from 'react';
import { DateType } from 'react-tailwindcss-datepicker';
import { getIdToken } from 'core/utils/getIdToken';
import { useNavigate } from 'react-router-dom';
import { IncidentsGetResponseSchema } from 'core/gen/schemas/IncidentsGetResponseSchema';

type ReturnValue = {
  /** 事案一覧のデータ。 */
  jianListData: Incident[] | undefined;
  /** ローディング中かどうか。 */
  isLoading: boolean;
  /** エラーが発生したかどうか。 */
  isError: boolean;
  /** 日付変更ハンドラ。 */
  handleDateChange: (startDate: DateType, endDate: DateType) => void;
  /** 検索ハンドラ。 */
  handleSearch: (value: string) => void;
  /** 検索フィールドの値。 */
  searchValue: string;
  /** 検索フィールドの値を設定する関数。 */
  setSearchValue: (value: string) => void;
  /** 選択された日付。 */
  selectedDate: {
    startDate: Date | null;
    endDate: Date | null;
  };
  /** 現在のページ。 */
  currentPageState: number;
  /** 現在のページを設定する関数。 */
  setCurrentPageState: (value: number) => void;
};

export const useJianList = (): ReturnValue => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const navigate = useNavigate();

  const initialSearch = params.get('search') ?? '';
  const initialPage = parsePositiveInteger(params.get('page'), firstPageNumber);
  const initialSelectedDate = parseSelectedDateParam(
    params.get('selected-date')
  );

  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [searchQueryParam, setSearchQueryParam] =
    useState<string>(initialSearch);
  const [currentPageState, setCurrentPageState] = useState<number>(initialPage);
  const [selectedDate, setSelectedDate] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(initialSelectedDate);

  // URLパラメータが変更されたときに検索条件と日付を更新
  useEffect(() => {
    const latestParams = new URLSearchParams(location.search);
    const nextSearch = latestParams.get('search') ?? '';
    const nextPage = parsePositiveInteger(
      latestParams.get('page'),
      firstPageNumber
    );
    const nextSelectedDate = parseSelectedDateParam(
      latestParams.get('selected-date')
    );

    setSearchValue(prev => (prev === nextSearch ? prev : nextSearch));
    setSearchQueryParam(prev => (prev === nextSearch ? prev : nextSearch));
    setCurrentPageState(prev => (prev === nextPage ? prev : nextPage));
    setSelectedDate(prev =>
      isSameDateRange(prev, nextSelectedDate) ? prev : nextSelectedDate
    );
  }, [location.search]);

  const {
    data: jianListData,
    isLoading,
    isError,
  } = useQuery<Incident[]>({
    queryKey: [
      'jian',
      formatDate(selectedDate.startDate),
      String(currentPageState),
      searchQueryParam,
    ],
    queryFn: async () => {
      const idToken = await getIdToken();
      const queryDate = selectedDate.startDate
        ? `?target_date=${formatDate(selectedDate.startDate)}`
        : `?target_date=${formatDate(new Date())}`;
      const searchParam = searchQueryParam
        ? `&search=${encodeURIComponent(searchQueryParam)}`
        : '';
      const response = await axios.get<IncidentsGetResponseSchema>(
        `${process.env.REACT_APP_API_HOST}/incidents${queryDate}${searchParam}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      return fetchResponseToIncident(response.data);
    },
  });

  // URLパラメータの更新
  const updateQueryParams = ({
    location,
    params,
  }: {
    location: ReturnType<typeof useLocation>;
    params: URLSearchParams;
  }) => {
    const newUrl = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    navigate(newUrl, { replace: true });
  };

  const handleDateChange = (startDate: DateType, endDate: DateType) => {
    setSelectedDate({ startDate, endDate });
    setCurrentPageState(firstPageNumber);

    if (startDate) {
      params.set('selected-date', formatDate(startDate));
    } else {
      params.delete('selected-date');
    }
    params.set('page', String(firstPageNumber));

    // 検索条件がある場合は保持
    if (searchQueryParam) {
      params.set('search', searchQueryParam);
    }

    updateQueryParams({ location, params });
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setSearchQueryParam(value);
    setCurrentPageState(firstPageNumber);

    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    params.set('page', String(firstPageNumber));
    updateQueryParams({ location, params });
  };

  return {
    jianListData,
    isLoading,
    isError,
    handleDateChange,
    handleSearch,
    searchValue,
    setSearchValue,
    selectedDate,
    currentPageState,
    setCurrentPageState,
  };
};

/** 1ページの番号。 */
const firstPageNumber = 1;

/** 1ページに表示する事案の数。 */
export const itemsPerPage = 10;

const formatDate = (date: DateType): string => {
  if (date === null) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fetchResponseToIncident = (
  response: IncidentsGetResponseSchema
): Incident[] => {
  return response.incidents.map(item => ({
    incidentId: item.incidentId,
    recognitionAt: item.recognitionAt ?? '',
    incidentNo: item.incidentNo ?? '',
    emsTeamName: item.emsTeamName ?? '',
    telegramTargetAddress: item.telegramTargetAddress ?? '',
    patients:
      item.patients?.map(patient => ({
        patientId: patient.patientId,
        age: patient.age ?? undefined,
        sex: patient.sex ?? '不明',
        patientName: patient.patientName ?? '傷病者',
        chiefComplaint: patient.chiefComplaint ?? '不明',
        receivingHospitalName: patient.receivingHospitalName ?? '不明',
      })) ?? [],
  }));
};

/**
 * URL クエリの `selected-date` を `Date` レンジに変換。
 * 不正または未指定の場合は当日レンジを返す。
 */
const parseSelectedDateParam = (selectedDateParam: string | null) => {
  const todayRange = createTodayRange();
  if (!selectedDateParam) {
    return todayRange;
  }

  const [year, month, day] = selectedDateParam.split('-').map(Number);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return todayRange;
  }

  const parsedDate = new Date(year, month - 1, day);
  return {
    startDate: parsedDate,
    endDate: parsedDate,
  };
};

/** 当日を基準にしたレンジを作成。 */
const createTodayRange = () => {
  const today = new Date();
  return normalizeRange(today);
};

/** 指定日付を 00:00 に正規化した単一日のレンジに変換。 */
const normalizeRange = (date: Date) => {
  const normalized = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  return {
    startDate: normalized,
    endDate: normalized,
  };
};

/** 2 つの日付レンジが同一日を指しているか比較。 */
const isSameDateRange = (
  prev: { startDate: Date | null; endDate: Date | null },
  next: { startDate: Date | null; endDate: Date | null }
) => {
  return (
    isSameDate(prev.startDate, next.startDate) &&
    isSameDate(prev.endDate, next.endDate)
  );
};

/** `Date` オブジェクトが同じ日付か判定。 */
const isSameDate = (a: Date | null, b: Date | null) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.getTime() === b.getTime();
};

/** 正の整数でない場合はフォールバック値を返す。 */
const parsePositiveInteger = (value: string | null, fallback: number) => {
  if (!value) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};
